import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SocialLinks from './components/SocialLinks';
import FooterLinkSection from './components/FooterLinkSection';
import ContactInfoCard from './components/ContactInfoCard';
import CertificationBadges from './components/CertificationBadges';
import NewsletterSignup from './components/NewsletterSignup';
import BackToTop from './components/BackToTop';
import { decodeProtectedData, PROTECTED_DATA } from '../../utils/obfuscation';
import type { SocialLink, FooterSection, ContactInfo, Certification } from './types';

const FooterSectionPage = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const socialLinks: SocialLink[] = [
    {
      name: 'GitHub',
      icon: 'Github',
      url: 'https://github.com/BMTmohammedtaha',
      ariaLabel: 'Visit GitHub profile',
    },
    {
      name: 'LinkedIn',
      icon: 'LinkedIn',
      url: 'https://linkedin.com/in/bmt-mohammedtaha/',
      ariaLabel: 'Visit LinkedIn profile',
    },
    {
      name: 'X',
      icon: 'X',
      url: 'https://x.com/BmtMohammedTaha',
      ariaLabel: 'Visit Twitter profile',
    },
    {
      name: 'Email',
      icon: 'Mail',
      url: 'mailto:info@mohammedtaha.site',
      ariaLabel: 'Send email',
    },
  ];

  const footerSections: FooterSection[] = [
    {
      title: t('footer.sections.services'),
      links: [
        { label: t('footer.links.fullStack'), path: '/portfolio-showcase' },
        { label: t('footer.links.sysArch'), path: '/portfolio-showcase' },
        { label: t('footer.links.ecom'), path: '/portfolio-showcase' },
        { label: t('footer.links.techConsulting'), path: '/contact-section' },
      ],
    },
    {
      title: t('footer.sections.company'),
      links: [
        { label: t('footer.links.aboutMe'), path: '/about-section' },
        { label: t('footer.links.portfolio'), path: '/portfolio-showcase' },
        { label: t('footer.links.testimonials'), path: '/testimonials-section' },
        { label: t('footer.links.contact'), path: '/contact-section' },
      ],
    },
    {
      title: t('footer.sections.legal'),
      links: [
        { label: t('footer.links.privacy'), path: '/privacy' },
        { label: t('footer.links.terms'), path: '/terms' },
        { label: t('footer.links.cookie'), path: '/privacy' },
        { label: t('footer.links.disclaimer'), path: '/terms' },
      ],
    },
  ];

  const contactInfo: ContactInfo[] = [
    {
      icon: 'Mail',
      label: t('footer.contacts.email'),
      value: decodeProtectedData(PROTECTED_DATA.email),
      href: decodeProtectedData(PROTECTED_DATA.emailHref),
    },
    {
      icon: 'Phone',
      label: t('footer.contacts.phone'),
      value: decodeProtectedData(PROTECTED_DATA.phone),
      href: decodeProtectedData(PROTECTED_DATA.phoneHref),
    },
    {
      icon: 'MapPin',
      label: t('footer.contacts.location'),
      value: t('footer.contacts.location') === 'Location' ? decodeProtectedData(PROTECTED_DATA.addressEn) : decodeProtectedData(PROTECTED_DATA.addressAr),
    },
    {
      icon: 'Clock',
      label: t('footer.contacts.businessHours'),
      value: t('footer.contacts.businessHoursVal'),
    },
  ];

  const certifications: Certification[] = [
    {
      name: t('footer.certs.ssl'),
      icon: 'Shield',
      description: t('footer.certs.sslDesc'),
    },
    {
      name: t('footer.certs.gdpr'),
      icon: 'CheckCircle',
      description: t('footer.certs.gdprDesc'),
    },
    {
      name: t('footer.certs.iso'),
      icon: 'Award',
      description: t('footer.certs.isoDesc'),
    },
    {
      name: t('footer.certs.pci'),
      icon: 'Lock',
      description: t('footer.certs.pciDesc'),
    },
  ];

  return (
    <>
      <div className="gradient-background">
          <footer className="glass-footer">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-cta flex items-center justify-center shadow-brand">
                        <img src="/logo.png" alt="mohammedTahaLogo" className="w-full h-full object-contain p-1" />
                      </div>
                    </div>
                    <p className="text-sm text-white/60 leading-relaxed">
                      {t('footer.desc')}
                    </p>
                  </div>
                  <SocialLinks links={socialLinks} />
                </div>

                {footerSections.map((section) => (
                  <FooterLinkSection key={section.title} section={section} />
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-12 pb-12 border-b border-white/10">
                <ContactInfoCard contacts={contactInfo} />
                <CertificationBadges certifications={certifications} />
                <NewsletterSignup />
              </div>

              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <p className="text-sm text-white/50">
                    &copy; {currentYear} {t('hero.name')}. {t('footer.rights')}
                  </p>
                  <p className="text-xs text-white/40 mt-2">
                    {t('footer.builtWith')}
                  </p>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-6">
                  <Link
                    to="/privacy"
                    className="text-sm text-white/50 hover:text-accent transition-colors duration-300"
                  >
                    {t('footer.links.privacy')}
                  </Link>
                  <span className="text-white/30">•</span>
                  <Link
                    to="/terms"
                    className="text-sm text-white/50 hover:text-accent transition-colors duration-300"
                  >
                    {t('footer.links.terms')}
                  </Link>
                  <span className="text-white/30">•</span>
                  <Link
                    to="/privacy"
                    className="text-sm text-white/50 hover:text-accent transition-colors duration-300"
                  >
                    {t('footer.links.cookie')}
                  </Link>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-white/10">
                <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-xs text-white/40">
                  <p>{t('footer.bottomLine1')}</p>
                  <span className="hidden md:inline">•</span>
                  <p>{t('footer.bottomLine2')}</p>
                  <span className="hidden md:inline">•</span>
                  <p>{t('footer.bottomLine3')}</p>
                </div>
              </div>
            </div>
          </footer>
      </div>
    </>
  );
};

export default FooterSectionPage;